import React, { useState } from 'react'
import { Box, TextField, Button, Grid, MenuItem } from '@mui/material'
import { misService } from '../../services/misService'

const MISForm: React.FC<{ initialData?: any, onSaved: () => void, onCancel: () => void }> = ({ initialData, onSaved, onCancel }) => {
  const [form, setForm] = useState<any>({
    RiderName: initialData?.RiderName ?? initialData?.riderName ?? '',
    riderPublicRiderId: initialData?.riderPublicRiderId || '',
    clientName: initialData?.clientName || '',
    clientRiderId: initialData?.clientRiderId || '',
    periodStartDate: initialData?.periodStartDate ? initialData.periodStartDate.split('T')[0] : '',
    periodEndDate: initialData?.periodEndDate ? initialData.periodEndDate.split('T')[0] : '',
    week: initialData?.week ?? '',
    Hub: initialData?.Hub ?? '',
    City: initialData?.City ?? '',
    PhoneNumber: initialData?.PhoneNumber ?? '',
    orders: initialData?.orders ?? '',
    remarks: initialData?.remarks ?? '',

    grossPayout: initialData?.grossPayout ?? '',
    tds: initialData?.tds ?? 0,
    clientDeduction: initialData?.clientDeduction ?? 0,
    sd: initialData?.sd ?? 0,
    damageCost: initialData?.damageCost ?? 0,
    fleet: initialData?.fleet ?? 0,
    trafficFine: initialData?.trafficFine ?? 0,
    damageCostDeducted: initialData?.damageCostDeducted ?? 0,
    trafficFineDeducted: initialData?.trafficFineDeducted ?? 0,
    batterySwap: initialData?.batterySwap ?? 0,
    onHold: initialData?.onHold ?? 0,
    others: initialData?.others ?? 0,
    cod: initialData?.cod ?? 0,
    evRent: initialData?.evRent ?? 0,
    evRentDeducted: initialData?.evRentDeducted ?? 0,
    Insurance: initialData?.Insurance ?? 0,

    status: initialData?.status ?? '',
    finalPayout: initialData?.finalPayout ?? '',
    pending: initialData?.pending ?? '',
    Deduction: initialData?.Deduction ?? '',
  })
  const [saving, setSaving] = useState(false)

  const onChange = (k: string, v: any) => setForm((s: any) => ({ ...s, [k]: v }))

  const handleSave = async () => {
    try {
      setSaving(true)
      // Prepare payload but omit computed-only fields: finalPayout and pending (server will compute them)
      const payload: any = { ...form };
      delete payload.finalPayout;
      delete payload.pending;
      delete payload.Deduction; // Deduction is computed on server

      if (initialData) {
        await misService.updateMIS(initialData.id, payload)
      } else {
        await misService.createMIS(payload)
      }
      onSaved()
    } catch (err: any) {
      alert(err?.response?.data?.error || err.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  // Compute client-side Deduction and predicted pending/final for clarity (server will validate/recompute)
  const sumDeductions = (Number(form.clientDeduction) || 0) + (Number(form.sd) || 0) + (Number(form.damageCost) || 0) + (Number(form.fleet) || 0) + (Number(form.trafficFine) || 0) + (Number(form.batterySwap) || 0) + (Number(form.onHold) || 0) + (Number(form.others) || 0) + (Number(form.cod) || 0) + (Number(form.evRent) || 0) + (Number(form.Insurance) || 0) + (Number(form.tds) || 0);
  const computedDeduction = sumDeductions - (Number(form.tds) || 0);
  const computedPending = computedDeduction > (Number(form.grossPayout) || 0) ? computedDeduction - (Number(form.grossPayout) || 0) : 0;
  const computedFinal = computedPending === 0 ? Math.max(0, (Number(form.grossPayout) || 0) - computedDeduction) : 0;

  return (
    <Box p={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField label="Rider Name" fullWidth value={form.RiderName} onChange={e => onChange('RiderName', e.target.value)} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Rider Public ID" fullWidth value={form.riderPublicRiderId} onChange={e => onChange('riderPublicRiderId', e.target.value)} />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField label="Client Name" fullWidth value={form.clientName} onChange={e => onChange('clientName', e.target.value)} />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField label="Client Rider ID" fullWidth value={form.clientRiderId} onChange={e => onChange('clientRiderId', e.target.value)} />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField type="date" label="Period Start" fullWidth InputLabelProps={{ shrink: true }} value={form.periodStartDate} onChange={e => onChange('periodStartDate', e.target.value)} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField type="date" label="Period End" fullWidth InputLabelProps={{ shrink: true }} value={form.periodEndDate} onChange={e => onChange('periodEndDate', e.target.value)} />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField label="Hub" fullWidth value={form.Hub} onChange={e => onChange('Hub', e.target.value)} />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField label="City" fullWidth value={form.City} onChange={e => onChange('City', e.target.value)} />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField label="Week" fullWidth value={form.week} onChange={e => onChange('week', e.target.value)} />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField label="Phone" fullWidth value={form.PhoneNumber} onChange={e => onChange('PhoneNumber', e.target.value)} />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Remarks"
            fullWidth
            multiline
            minRows={2}
            value={form.remarks}
            onChange={e => onChange('remarks', e.target.value)}
          />
        </Grid>

        {initialData && (
          <Grid item xs={12} sm={4}>
            <TextField select label="Status" fullWidth value={form.status || ''} onChange={e => onChange('status', e.target.value)}>
              {['PENDING', 'DEDUCTED', 'PAID', 'HOLD', 'NULL'].map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        <Grid item xs={12} sm={4}>
          <TextField type="number" label="Orders" fullWidth value={form.orders} onChange={e => onChange('orders', e.target.value === '' ? '' : parseInt(e.target.value, 10))} />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField type="number" label="Gross Payout" fullWidth value={form.grossPayout} onChange={e => onChange('grossPayout', e.target.value === '' ? '' : parseFloat(e.target.value))} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField type="number" label="TDS" fullWidth value={form.tds} onChange={e => onChange('tds', e.target.value === '' ? '' : parseFloat(e.target.value))} />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField type="number" label="Client Deduction" fullWidth value={form.clientDeduction} onChange={e => onChange('clientDeduction', e.target.value === '' ? '' : parseFloat(e.target.value))} />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            type="number"
            label="SD"
            fullWidth
            value={form.sd}
            onChange={e => onChange('sd', e.target.value === '' ? '' : parseFloat(e.target.value))}
            disabled={!!initialData}
            helperText={initialData ? 'SD cannot be changed after creation' : ''}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField type="number" label="Damage Cost" fullWidth value={form.damageCost} onChange={e => onChange('damageCost', e.target.value === '' ? '' : parseFloat(e.target.value))} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField type="number" label="Damage Cost Deducted" fullWidth value={form.damageCostDeducted} onChange={e => onChange('damageCostDeducted', e.target.value === '' ? '' : parseFloat(e.target.value))} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField type="number" label="Fleet" fullWidth value={form.fleet} onChange={e => onChange('fleet', e.target.value === '' ? '' : parseFloat(e.target.value))} />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField type="number" label="Traffic Fine" fullWidth value={form.trafficFine} onChange={e => onChange('trafficFine', e.target.value === '' ? '' : parseFloat(e.target.value))} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField type="number" label="Traffic Fine Deducted" fullWidth value={form.trafficFineDeducted} onChange={e => onChange('trafficFineDeducted', e.target.value === '' ? '' : parseFloat(e.target.value))} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField type="number" label="Battery Swap" fullWidth value={form.batterySwap} onChange={e => onChange('batterySwap', e.target.value === '' ? '' : parseFloat(e.target.value))} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField type="number" label="On Hold" fullWidth value={form.onHold} onChange={e => onChange('onHold', e.target.value === '' ? '' : parseFloat(e.target.value))} />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField type="number" label="Others" fullWidth value={form.others} onChange={e => onChange('others', e.target.value === '' ? '' : parseFloat(e.target.value))} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField type="number" label="COD" fullWidth value={form.cod} onChange={e => onChange('cod', e.target.value === '' ? '' : parseFloat(e.target.value))} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField type="number" label="EV Rent" fullWidth value={form.evRent} onChange={e => onChange('evRent', e.target.value === '' ? '' : parseFloat(e.target.value))} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField type="number" label="EV Rent Deducted" fullWidth value={form.evRentDeducted} onChange={e => onChange('evRentDeducted', e.target.value === '' ? '' : parseFloat(e.target.value))} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField type="number" label="Insurance" fullWidth value={form.Insurance} onChange={e => onChange('Insurance', e.target.value === '' ? '' : parseFloat(e.target.value))} />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField label="Computed Final Payout" fullWidth value={computedFinal} disabled />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField label="Computed Pending" fullWidth value={computedPending} disabled />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField label="Deduction (computed)" fullWidth value={computedDeduction} disabled />
        </Grid>

        <Grid item xs={12} style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </Grid>
      </Grid>
    </Box>
  )
}

export default MISForm
